// src/pages/InternalRegulationPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import * as Lucide from "lucide-react";
import { Banner } from "../../../components/common/Banner";
import {
  getIRConfig,
  getLatestIRDoc,
  getIRContent,
  downloadIRDocUrl,
} from "../../../services/internalregulationSevice";

/* -------- helpers -------- */
const isAbsUrl = (u) => /^https?:\/\//i.test(String(u || ""));
const absoluteUrl = (u) => (isAbsUrl(u) ? u : String(u || ""));
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};
const resolveIcon = (name) => (name && Lucide[name]) || Lucide.FileText;

/* Miniatura: usa coverUrl; si no hay, embebe 1ª página del PDF */
function CoverPreview({ coverSrc, pdfHref, alt = "Portada", size = "sm" }) {
  const [imgOk, setImgOk] = useState(Boolean(coverSrc));

  const sizeCls =
    size === "xs"
      ? "w-[80px] h-[112px] sm:w-[100px] sm:h-[140px]"
      : size === "sm"
      ? "w-[120px] h-[160px] sm:w-[140px] sm:h-[190px] md:w-[160px] md:h-[220px]"
      : size === "md"
      ? "w-[140px] h-[190px] sm:w-[160px] sm:h-[220px] md:w-[180px] md:h-[250px]"
      : "w-[160px] h-[220px] sm:w-[180px] sm:h-[250px] md:w-[200px] md:h-[280px]"; // lg

  const canEmbed = Boolean(pdfHref);

  return (
    <div
      className={`${sizeCls} rounded-xl border border-blue-100 bg-white overflow-hidden flex items-center justify-center shadow-sm`}
    >
      {imgOk && coverSrc ? (
        <img
          src={absoluteUrl(coverSrc)}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImgOk(false)}
          loading="lazy"
        />
      ) : canEmbed ? (
        <embed
          src={`${absoluteUrl(pdfHref)}#page=1&zoom=105`}
          type="application/pdf"
          className="w-full h-full block"
        />
      ) : null}
    </div>
  );
}

function GradientCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl p-[1.5px] ${className}`}
      style={{ background: "linear-gradient(135deg,#E6F0FF 0%,#E6FBFF 100%)" }}
    >
      <div className="rounded-2xl bg-white ring-1 ring-black/5">{children}</div>
    </div>
  );
}

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-[#0B4A8F] border border-blue-100 px-3 py-1 text-sm font-medium">
    {children}
  </span>
);

export default function InternalRegulationPage() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({
    pageTitle: "Reglamento interno",
    subtitle: "",
    principlesVisible: true,
    lastVisibleAt: null,
  });
  const [content, setContent] = useState({
    lastReview: "",
    principles: [],
    sections: [],
  });
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [c, cnt, d] = await Promise.all([
          getIRConfig().catch(() => null),
          getIRContent().catch(() => null),
          getLatestIRDoc().catch(() => null), // solo publicado
        ]);

        if (c && typeof c === "object") {
          setCfg({
            pageTitle: String(c.pageTitle || "Reglamento interno"),
            subtitle: String(c.subtitle || ""),
            principlesVisible:
              c.principlesVisible === undefined ? true : !!c.principlesVisible,
            lastVisibleAt: c.lastVisibleAt || null,
          });
        }

        if (cnt && typeof cnt === "object") {
          setContent({
            lastReview: String(cnt.lastReview || ""),
            principles: Array.isArray(cnt.principles) ? cnt.principles : [],
            sections: Array.isArray(cnt.sections) ? cnt.sections : [],
          });
        }

        // d es null si no hay doc publicado
        setDoc(
          d && typeof d === "object"
            ? {
                id: d.id || d._id || "",
                title: d.title || "Reglamento interno",
                versionTag: d.versionTag || "",
                secureUrl: d.secureUrl || "",
                coverUrl: d.coverUrl || "",
                createdAt: d.createdAt || "",
                bytes: d.bytes || undefined,
              }
            : null
        );
      } catch (e) {
        setError(e?.message || "No se pudo cargar el reglamento interno.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-xl text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const pdfViewHref = doc?.secureUrl?.trim()
    ? absoluteUrl(doc.secureUrl)
    : doc?.id
    ? downloadIRDocUrl(doc.id)
    : null;
  const coverUrl = doc?.coverUrl ? absoluteUrl(doc.coverUrl) : null;
  const hasVisibleDoc = Boolean(pdfViewHref && doc); // gate del hero

  const hasMeta =
    cfg.lastVisibleAt || content.lastReview || (doc && doc.versionTag);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-rose-50">
      <div className="max-w-screen-xl mx-auto px-4 py-8 space-y-8">
        <Banner
          title={cfg.pageTitle || "Reglamento interno"}
          message={cfg.subtitle || "Normativa y reglas internas de la organización"}
          className="mb-6"
        />

        {/* Badges superiores (meta) */}
        {hasMeta && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {cfg.lastVisibleAt && (
              <Pill>Última actualización: {fmtDateTime(cfg.lastVisibleAt)}</Pill>
            )}

            {cfg.lastVisibleAt && (content.lastReview || doc?.versionTag) && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />
            )}

            {content.lastReview && (
              <>
                <Pill>Última revisión: {content.lastReview}</Pill>
                {doc?.versionTag && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />
                )}
              </>
            )}

            {doc?.versionTag && (
              <Pill>Versión PDF: {doc.versionTag}</Pill>
            )}
          </div>
        )}

        {/* HERO: portada + título + botón (solo si hay doc publicado) */}
        {hasVisibleDoc && (
          <div className="rounded-3xl bg-gradient-to-t from-[#0EC5DF] via-[#0488AF] to-[#0B4A8F] text-white px-5 py-6 md:px-7 md:py-7 mb-10 shadow-sm">
            <div className="grid gap-5 md:gap-7 md:grid-cols-[minmax(240px,280px),minmax(0,1fr)] items-start">
              {/* Portada */}
              <div className="w-full max-w-[280px] justify-self-center md:justify-self-start">
                <div className="aspect-[3/4] rounded-2xl bg-white overflow-hidden shadow-md">
                  {coverUrl ? (
                    <img
                      className="w-full h-full object-cover"
                      src={coverUrl}
                      alt="Portada Reglamento interno"
                      onError={(e) => {
                        if (pdfViewHref) {
                          e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${pdfViewHref}#page=1&zoom=125" style="width:100%;height:100%;display:block;border:0" />`;
                        }
                      }}
                    />
                  ) : pdfViewHref ? (
                    <embed
                      type="application/pdf"
                      src={`${pdfViewHref}#page=1&zoom=125`}
                      style={{ width: '100%', height: '100%', display: 'block', border: 0 }}
                    />
                  ) : null}
                </div>
              </div>

              {/* Título + botón */}
              <div className="w-full min-w-0">
                <div className="text-xl md:text-2xl font-semibold mb-3 text-white">
                  {(doc?.title || "").trim() || "Reglamento interno"}
                </div>
                {pdfViewHref && (
                  <a
                    href={pdfViewHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/95 text-[#0488AF] hover:bg-white font-medium shadow-sm"
                  >
                    <Lucide.FileDown size={18} />
                    Ver / Descargar Reglamento interno
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PRINCIPIOS (respeta principlesVisible) */}
        {cfg.principlesVisible &&
          Array.isArray(content.principles) &&
          content.principles.length > 0 && (
            <GradientCard>
              <div className="p-5">
                <h3 className="text-lg font-semibold mb-3">Principios</h3>
                <div className="flex flex-wrap gap-2">
                  {content.principles.map((p, i) => (
                    <Pill key={i}>
                      <Lucide.CheckCircle2 className="w-4 h-4" />
                      {p}
                    </Pill>
                  ))}
                </div>
              </div>
            </GradientCard>
          )}

        {/* SECCIONES */}
        {Array.isArray(content.sections) && content.sections.length > 0 ? (
          <div className="space-y-6">
            {content.sections.map((sec, i) => {
              const Icon = resolveIcon(sec.icon);
              const hasBody = Array.isArray(sec.body) && sec.body.length > 0;
              const hasBullets =
                Array.isArray(sec.bullets) && sec.bullets.length > 0;

              // CTA (nuevo esquema o legado)
              const singleLabel = sec?.cta?.label?.trim();
              const singleUrl = sec?.cta?.url?.trim();
              const legacyReport = sec?.cta?.reportUrl?.trim();
              const legacyPolicy = sec?.cta?.policyUrl?.trim();

              return (
                <GradientCard key={i}>
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="px-2 py-1 rounded-lg bg-blue-50 text-[#0B4A8F] text-xs font-semibold">
                        Sección {i + 1}
                      </span>
                      {sec.iconImageUrl ? (
                        <img
                          src={sec.iconImageUrl}
                          alt=""
                          className="h-6 w-6 object-contain rounded border border-blue-100 bg-white"
                        />
                      ) : (
                        <Icon className="w-5 h-5 text-[#0B4A8F]" />
                      )}
                      <h4 className="text-lg font-semibold text-gray-800 truncate">
                        {sec.title || "Sección"}
                      </h4>
                    </div>

                    {hasBody && (
                      <div className="space-y-2 text-gray-700">
                        {sec.body.map((p, idx) => (
                          <p key={idx} className="leading-relaxed">
                            {p}
                          </p>
                        ))}
                      </div>
                    )}

                    {hasBullets && (
                      <ul className="list-disc ml-6 text-gray-700 space-y-1">
                        {sec.bullets.map((b, idx) => (
                          <li key={idx}>{b}</li>
                        ))}
                      </ul>
                    )}

                    {/* CTA dentro de la sección */}
                    {(sec.cta?.reportUrl ||
                      sec.cta?.policyUrl ||
                      sec.cta?.contactUrl ||
                      (singleLabel && singleUrl)) && (
                      <div className="pt-2 flex flex-wrap gap-2">
                        {/* Nuevo esquema: un botón configurable */}
                        {singleLabel && singleUrl && (
                          <a
                            href={singleUrl}
                            target={
                              singleUrl.startsWith("/") ||
                              /^mailto:/i.test(singleUrl)
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                          >
                            <Lucide.ExternalLink size={16} />
                            {singleLabel}
                          </a>
                        )}

                        {/* Botones estándar: reportUrl, policyUrl, contactUrl */}
                        {sec.cta?.reportUrl && (
                          <a
                            href={sec.cta.reportUrl}
                            target={
                              sec.cta.reportUrl.startsWith("/") ||
                              /^mailto:/i.test(sec.cta.reportUrl)
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                          >
                            <Lucide.Megaphone size={16} />
                            Reportar una inquietud
                          </a>
                        )}
                        {sec.cta?.policyUrl && (
                          <a
                            href={sec.cta.policyUrl}
                            target={
                              sec.cta.policyUrl.startsWith("/")
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
                          >
                            <Lucide.ExternalLink size={16} />
                            Ver política
                          </a>
                        )}
                        {sec.cta?.contactUrl && (
                          <a
                            href={sec.cta.contactUrl}
                            target={
                              sec.cta.contactUrl.startsWith("/") ||
                              /^mailto:/i.test(sec.cta.contactUrl)
                                ? "_self"
                                : "_blank"
                            }
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                          >
                            {/^mailto:/i.test(sec.cta.contactUrl) ? (
                              <Lucide.Mail size={16} />
                            ) : (
                              <Lucide.ExternalLink size={16} />
                            )}
                            Contacto
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </GradientCard>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-500">No hay secciones para mostrar.</div>
        )}
      </div>
    </div>
  );
}
